import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Box, Stack } from '@mui/material';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import { Input } from '@liminal/web-ui/forms/Input';
import { Button } from '@liminal/web-ui/primitives/Button';
import type { EventGuest, EventGuestLookup } from '@/api/types';
import { QrScanner } from '@/components/scanner/QrScanner';
import { useEventCode, useIssueEventTickets, useResolveEventGuest } from '@/hooks/useEvents';
import { useNow } from '@/hooks/useNow';
import {
  NOT_A_PROFILE_QR,
  SEARCH_HINT,
  issueFailure,
  lookupFromScan,
  lookupFromSearch,
  lookupProblem,
  type IssueOutcome,
} from '../checkIn';
import { GuestCard } from '../components/GuestCard';

/** The card's "Issued 21:14" is read against a clock this fresh. */
const CLOCK_TICK_MS = 30_000;

/**
 * The door (LIM-4707): scan a guest's profile QR, or type their email, @tag
 * or user id, see whether they may be handed tickets, and hand them over with
 * one tap.
 *
 * A new scan or search replaces the card, and the card is cleared while the
 * next guest loads, so the button on screen is always for the guest whose
 * name is above it. Scans are ignored while a tap is in flight for the same
 * reason.
 */
export function CheckInPage() {
  const code = useEventCode();
  const now = useNow(CLOCK_TICK_MS);
  const resolve = useResolveEventGuest(code);
  const issue = useIssueEventTickets(code);

  const [guest, setGuest] = useState<EventGuest | null>(null);
  const [outcome, setOutcome] = useState<IssueOutcome | null>(null);
  const [scanRefused, setScanRefused] = useState(false);
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState<string | undefined>(undefined);
  const cardRef = useRef<HTMLDivElement>(null);

  // A guest who has just loaded is scrolled to, so the button is on screen
  // below the viewfinder. jsdom has no scrollIntoView.
  const shownUserId = guest?.userId;
  useEffect(() => {
    if (shownUserId !== undefined) cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [shownUserId]);

  const lookUp = (lookup: EventGuestLookup) => {
    setGuest(null);
    setOutcome(null);
    resolve.mutate(lookup, { onSuccess: (found) => setGuest(found) });
  };

  const onDecode = (payload: string) => {
    if (issue.isPending) return;
    const lookup = lookupFromScan(payload);
    setScanRefused(lookup === null);
    if (lookup !== null) lookUp(lookup);
  };

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    const lookup = lookupFromSearch(search);
    setSearchError(lookup === null ? SEARCH_HINT : undefined);
    if (lookup !== null) lookUp(lookup);
  };

  const onIssue = () => {
    if (guest === null) return;
    const tappedFor = guest;
    setOutcome(null);
    issue.mutate(tappedFor.userId, {
      onSuccess: (after) => {
        setGuest(after);
        setOutcome({ kind: 'issued' });
      },
      onError: (error) => {
        const failure = issueFailure(error, tappedFor.event, Date.now());
        if (!failure.reread) {
          setOutcome(failure.outcome);
          return;
        }
        // The server says the card is out of date: read the guest again and
        // say what came of the tap over what they hold now.
        resolve.mutate(
          { by: 'userId', value: tappedFor.userId },
          {
            onSuccess: (fresh) => {
              setGuest(fresh);
              setOutcome(failure.outcome);
            },
          },
        );
      },
    });
  };

  const problem = resolve.isError ? lookupProblem(resolve.error) : null;
  const retryLookup = resolve.variables;

  return (
    <Stack spacing={{ xs: 2, sm: 3 }}>
      <Stack spacing={1}>
        <QrScanner onDecode={onDecode} />
        {scanRefused ? (
          <WarningCard tone="warning" compact data-testid="scan-refused">
            {NOT_A_PROFILE_QR}
          </WarningCard>
        ) : null}
      </Stack>

      <Box ref={cardRef}>
        {resolve.isPending ? <LoadingSpinner text="Finding the guest…" /> : null}
        {problem ? (
          <ErrorState
            title={problem.title}
            message={problem.message}
            onRetry={problem.retry && retryLookup !== undefined ? () => lookUp(retryLookup) : undefined}
            data-testid="guest-lookup-failed"
          />
        ) : null}
        {!resolve.isPending && !problem && guest !== null ? (
          <GuestCard guest={guest} outcome={outcome} now={now} issuing={issue.isPending} onIssue={onIssue} />
        ) : null}
      </Box>

      <Box component="form" onSubmit={onSearch}>
        <Stack spacing={1}>
          <Input
            label="Can't scan? Find the guest"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setSearchError(undefined);
            }}
            placeholder="Email, @tag or user id"
            error={searchError}
            autoComplete="off"
          />
          <Button type="submit" variant="secondary" size="lg" fullWidth disabled={issue.isPending}>
            Find guest
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
